import { motion } from "framer-motion";
import {
    ArrowRight, Bell, CheckCircle2, Heart, Image as ImageIcon, MessageCircle, Play, Search, Send, Star,
} from "lucide-react";
import { BlackCTA, EASE, LazyVideo, Reveal, Section } from "./shared";

const ADS = [
    { src: "/assets/ad-1.mp4", label: "Festive offer · Jewellery", aspect: "aspect-[9/16]" },
    { src: "/assets/ad-2.mp4", label: "Founder story · Services", aspect: "aspect-[3/4]" },
    { src: "/assets/ad-3.mp4", label: "Product film · Construction", aspect: "aspect-[4/5]" },
    { src: "/assets/ad-4.mp4", label: "App promo · UGC-style", aspect: "aspect-[9/16]" },
    { src: "/assets/ad-5.mp4", label: "Talking head · Services", aspect: "aspect-[3/4]" },
];

export const VideoAds = () => (
    <Section id="what-you-can-do">
        <Reveal className="max-w-3xl">
            <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                Make professional and high quality video ads for your business.
            </h2>
            <p className="mt-6 max-w-2xl text-base text-neutral-600 sm:text-lg">
                From a rough idea to a finished vertical video — hooks, scripts and edits shaped around your offer,
                ready for Google and Meta.
            </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 items-start gap-4 md:grid-cols-3">
            {ADS.map((a, i) => (
                <div
                    key={a.src}
                    data-testid={`ad-video-${i}`}
                    className={`group relative overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-neutral-100 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${a.aspect}`}
                >
                    <LazyVideo src={a.src} className="absolute inset-0 h-full w-full object-cover" />
                    <span className="absolute bottom-3 left-3 rounded-full bg-white/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink backdrop-blur-sm">
                        {a.label}
                    </span>
                </div>
            ))}
        </div>

        <div className="mt-10 text-center">
            <BlackCTA testId="video-ads-cta">Make my first video ad</BlackCTA>
        </div>
    </Section>
);

const SearchAdMock = () => (
    <div className="flex h-36 flex-col justify-center bg-paper px-5">
        <p className="text-[10px] font-bold text-neutral-500">Sponsored</p>
        <p className="mt-1 text-[11px] text-neutral-500">purplebusiness.in/city-salon</p>
        <p className="mt-0.5 font-display text-sm font-bold text-ink">Hair Spa This Week — Weekday Slots | City Salon</p>
        <p className="mt-1 text-[11px] leading-snug text-neutral-500">Book a weekday slot in two taps. First visit includes a free consultation.</p>
    </div>
);

const BannerMock = () => (
    <div className="flex h-36 flex-col items-start justify-center bg-ink px-6">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">Neighbourhood Café</p>
        <p className="mt-1 font-display text-xl font-bold text-white">Monsoon Bundle · ₹199</p>
        <span className="mt-3 rounded-full bg-white px-4 py-1.5 text-xs font-bold text-ink">Order now</span>
    </div>
);

const VideoMock = ({ hook }) => (
    <div className="relative flex h-36 items-end bg-gradient-to-br from-neutral-700 to-ink p-4">
        <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-0.5 h-5 w-5 fill-white text-white" />
        </span>
        <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">Opening hook · 0–3s</p>
            <p className="font-display text-base font-bold leading-snug text-white">{hook}</p>
        </div>
    </div>
);

const UgcMock = () => (
    <div className="flex h-36 flex-col justify-center bg-paper px-5">
        <div className="rounded-2xl bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-white">R</span>
                <p className="text-[11px] font-bold text-ink">a regular customer</p>
            </div>
            <p className="mt-2 text-xs italic leading-snug text-neutral-600">“I found this place by accident and now I come every weekend…”</p>
        </div>
        <div className="mt-2.5 flex items-center gap-3 text-neutral-400">
            <Heart className="h-3.5 w-3.5" /><MessageCircle className="h-3.5 w-3.5" /><Send className="h-3.5 w-3.5" />
        </div>
    </div>
);

const GooglePostMock = () => (
    <div className="flex h-36 flex-col justify-center bg-paper px-5">
        <div className="rounded-2xl bg-white p-3.5 shadow-sm">
            <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-ink font-display text-xs font-bold text-white">D</span>
                <div>
                    <p className="text-[11px] font-bold text-ink">Sample Dental Clinic</p>
                    <p className="text-[10px] text-neutral-400">Google Business Profile · 2h ago</p>
                </div>
            </div>
            <p className="mt-2 text-xs font-bold text-ink">Now open on Sundays. Walk-ins welcome.</p>
            <p className="mt-1 text-[11px] font-bold text-neutral-500 underline underline-offset-2">Learn more</p>
        </div>
    </div>
);

const ScriptMock = () => (
    <div className="flex h-36 items-center gap-2 bg-paper px-4">
        {["0–3s", "3–8s", "8–15s"].map((t, i) => (
            <div key={t} className="flex-1 rounded-xl bg-white p-2.5 shadow-sm">
                <div className="flex h-9 items-center justify-center rounded-md bg-ink/90">
                    <Play className="h-3.5 w-3.5 fill-white text-white" />
                </div>
                <p className="mt-1.5 text-[9px] font-bold text-neutral-400">{t}</p>
                <p className="text-[10px] font-bold text-ink">{["Fresh counter", "Regulars", "Today’s special"][i]}</p>
            </div>
        ))}
    </div>
);

const SAMPLES = [
    { type: "Search ad", biz: "Sample · City Salon", body: "Hair spa this week — book a weekday slot in two taps.", icon: Search, Mock: SearchAdMock },
    { type: "Banner", biz: "Sample · Neighbourhood Café", body: "Monsoon bundle: filter coffee + warm brownies.", icon: ImageIcon, Mock: BannerMock },
    { type: "Vertical video", biz: "Sample · Fitness Studio", body: "Hook to test: ‘3 mornings that change your week’.", icon: Play, Mock: () => <VideoMock hook="“3 mornings that change your week”" /> },
    { type: "UGC-style concept", biz: "Sample · Boutique", body: "‘I found this place by accident…’ — a natural-sounding script draft.", icon: Play, Mock: UgcMock },
    { type: "Google Business post", biz: "Sample · Dental Clinic", body: "Now open on Sundays. Walk-ins welcome.", icon: Bell, Mock: GooglePostMock },
    { type: "Video script", biz: "Sample · Bakery", body: "15s: fresh counter → regulars → today’s special.", icon: Play, Mock: ScriptMock },
];

export const CreativeWall = () => (
    <Section>
        <Reveal className="max-w-3xl">
            <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                Create search ads, banner ads, video ads, UGC-style content, and Google Business posts.
            </h2>
            <p className="mt-6 max-w-2xl text-base text-neutral-600 sm:text-lg">
                Create around the offer you want to make. Purple helps with the writing, design and editing — so every
                new campaign doesn’t require another production brief.
            </p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SAMPLES.map((s, i) => (
                <Reveal key={s.type} delay={(i % 3) * 0.08}>
                    <div
                        data-testid={`creative-sample-${i}`}
                        className="group h-full overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-ink hover:shadow-xl"
                    >
                        <s.Mock />
                        <div className="p-6">
                            <div className="flex items-center justify-between">
                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-paper text-ink">
                                    <s.icon className="h-5 w-5" />
                                </span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400">Sample concept</span>
                            </div>
                            <p className="mt-5 font-display text-lg font-bold text-ink">{s.type}</p>
                            <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-neutral-500">{s.biz}</p>
                            <p className="mt-3 text-sm leading-relaxed text-neutral-600">{s.body}</p>
                        </div>
                    </div>
                </Reveal>
            ))}
        </div>

        <Reveal delay={0.1} className="mt-10 flex flex-col items-start gap-3 text-left sm:flex-row sm:items-center sm:justify-center sm:gap-6 sm:text-center">
            {["Choose an idea", "Make your version", "Review and launch"].map((step, i) => (
                <span key={step} className="flex items-center gap-3 font-display text-lg font-bold text-ink sm:gap-6">
                    <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-sm text-white">{i + 1}</span>
                        {step}
                    </span>
                    {i < 2 && <ArrowRight className="hidden h-5 w-5 text-neutral-300 sm:block" />}
                </span>
            ))}
        </Reveal>

        <div className="mt-12 text-center sm:text-center">
            <BlackCTA testId="creative-wall-cta">Become a Purple Business</BlackCTA>
        </div>
    </Section>
);
